import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  TablePagination,
  InputAdornment,
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
  Divider,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from "@mui/icons-material/Add";
import { locationService, storeLocationMappingService, organizationService } from '../services'
import { formatDateTime } from '../utils/dateFormat'
import { formatOrganizationLabel } from '../utils/organizationLabel'

const SEARCH_DEBOUNCE_MS = 450

function orgRowLabel(row) {
  const o = row?.organization
  if (!o) return '—'
  return formatOrganizationLabel(o)
}

function useDebouncedValue(value, ms) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

/** Add org+location links (batch create). */
function AddOrgMappingDialog({ open, onClose, onSaved }) {
  const [organization, setOrganization] = useState(null)
  const [orgInput, setOrgInput] = useState('')
  const debouncedOrgInput = useDebouncedValue(orgInput, SEARCH_DEBOUNCE_MS)
  const [orgOptions, setOrgOptions] = useState([])
  const [orgLoading, setOrgLoading] = useState(false)

  const [locSearch, setLocSearch] = useState('')
  const debouncedLocSearch = useDebouncedValue(locSearch, SEARCH_DEBOUNCE_MS)
  const [locPage, setLocPage] = useState(0)
  const [locRowsPerPage, setLocRowsPerPage] = useState(10)
  const [locTotal, setLocTotal] = useState(0)
  const [locPageItems, setLocPageItems] = useState([])
  const [locLoading, setLocLoading] = useState(false)

  const [selectedLocations, setSelectedLocations] = useState(() => new Map())

  const [actionError, setActionError] = useState(null)
  const [saveLoading, setSaveLoading] = useState(false)

  const resetForm = useCallback(() => {
    setOrganization(null);
    setOrgInput("");
    setOrgOptions([]);
    setLocSearch("");
    setLocPage(0);
    setLocTotal(0);
    setLocPageItems([]);
    setSelectedLocations(new Map());
    setActionError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setOrgLoading(true);
        const q = debouncedOrgInput.trim();
        const { items } = await organizationService.getOrganizationsPage({
          page: 1,
          limit: 50,
          ...(q ? { search: q } : {}),
        });
        if (cancelled) return;
        setOrgOptions(items);
      } catch {
        if (!cancelled) setOrgOptions([]);
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, debouncedOrgInput]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLocLoading(true);
        const q = debouncedLocSearch.trim();
        const data = await locationService.getLocations({
          page: locPage + 1,
          limit: locRowsPerPage,
          ...(q ? { search: q } : {}),
        });
        if (cancelled) return;
        setLocPageItems(data?.items || []);
        setLocTotal(data?.pagination?.total ?? 0);
      } catch {
        if (!cancelled) {
          setLocPageItems([]);
          setLocTotal(0);
        }
      } finally {
        if (!cancelled) setLocLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, locPage, locRowsPerPage, debouncedLocSearch]);

  useEffect(() => {
    if (!open) return;
    setLocPage(0);
  }, [debouncedLocSearch, open]);

  const toggleLoc = useCallback((loc, checked) => {
    setSelectedLocations((prev) => {
      const next = new Map(prev);
      if (checked)
        next.set(loc.id, { id: loc.id, name: loc.name, code: loc.code });
      else next.delete(loc.id);
      return next;
    });
  }, []);

  const removeChip = useCallback((id) => {
    setSelectedLocations((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleSave = async () => {
    setActionError(null);
    if (!organization) {
      setActionError("Select a shop");
      return;
    }
    const locationIds = Array.from(selectedLocations.keys());
    if (locationIds.length === 0) {
      setActionError("Select at least one location");
      return;
    }
    try {
      setSaveLoading(true);
      const res = await storeLocationMappingService.create({
        organization_id: organization.id,
        location_ids: locationIds,
      });
      const msg = `Added ${res.inserted ?? 0} link(s).${
        res.skipped_duplicates
          ? ` Skipped ${res.skipped_duplicates} duplicate(s).`
          : ""
      }`;
      onSaved?.(msg);
      onClose();
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        (typeof e.response?.data === "string" ? e.response.data : null) ||
        e.message ||
        "Save failed";
      setActionError(Array.isArray(msg) ? msg.join(", ") : String(msg));
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: "#f8f9fb" }}>
        Add shop locations
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <>
          {actionError && (
            <Alert
              severity="error"
              onClose={() => setActionError(null)}
              sx={{ mb: 2 }}
            >
              {actionError}
            </Alert>
          )}

          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Shop Id
          </Typography>
          <Autocomplete
            value={organization}
            onChange={(_, v) => {
              setOrganization(v);
              if (v) setOrgInput(formatOrganizationLabel(v));
              else setOrgInput("");
            }}
            inputValue={orgInput}
            onInputChange={(_, v, reason) => {
              if (reason === "input" || reason === "clear") setOrgInput(v);
            }}
            options={orgOptions}
            loading={orgLoading}
            getOptionLabel={(o) => formatOrganizationLabel(o)}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            filterOptions={(x) => x}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  {formatOrganizationLabel(option)}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search shop name or Shop Id…"
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {orgLoading ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Locations to link
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 1 }}
          >
            One table row per shop and location. Duplicates for the same
            pair are skipped.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 2,
              minHeight: 32,
            }}
          >
            {selectedLocations.size === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None selected
              </Typography>
            ) : (
              Array.from(selectedLocations.values()).map((loc) => (
                <Chip
                  key={loc.id}
                  label={`${loc.name} (${loc.code})`}
                  size="small"
                  onDelete={() => removeChip(loc.id)}
                />
              ))
            )}
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Search locations"
            value={locSearch}
            onChange={(e) => setLocSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#666" }} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Paper
            variant="outlined"
            sx={{ p: 1, maxHeight: 280, overflow: "auto" }}
          >
            {locLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : locPageItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No locations match this search.
              </Typography>
            ) : (
              locPageItems.map((loc) => {
                const checked = selectedLocations.has(loc.id);
                return (
                  <FormControlLabel
                    key={loc.id}
                    sx={{ display: "flex", ml: 0, py: 0.25, px: 1 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => toggleLoc(loc, e.target.checked)}
                      />
                    }
                    label={`${loc.name} (${loc.code})`}
                  />
                );
              })
            )}
          </Paper>
          <TablePagination
            component="div"
            count={locTotal}
            page={locPage}
            onPageChange={(_, p) => setLocPage(p)}
            rowsPerPage={locRowsPerPage}
            onRowsPerPageChange={(e) => {
              setLocRowsPerPage(parseInt(e.target.value, 10));
              setLocPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={saveLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            saveLoading || !organization || selectedLocations.size === 0
          }
          sx={{ bgcolor: "#e90000", "&:hover": { bgcolor: "#cc0000" } }}
        >
          {saveLoading ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const StoreLocationMappingPage = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS)
  const [searchLoading, setSearchLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const [successMessage, setSuccessMessage] = useState(null)

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      }
      if (debouncedSearchQuery.trim()) {
        params.search = debouncedSearchQuery.trim()
      }
      const data = await storeLocationMappingService.listOrganizations(params)
      setRows(data?.items || [])
      setTotalItems(data?.pagination?.total ?? 0)
    } catch (e) {
      console.error(e)
      setRows([])
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, debouncedSearchQuery])

  useEffect(() => {
    fetchOrganizations()
  }, [fetchOrganizations])

  useEffect(() => {
    setPage(0)
  }, [debouncedSearchQuery])

  const handleSearchSubmit = async (e) => {
    e.preventDefault()
    setPage(0)
    setSearchLoading(true)
    try {
      await fetchOrganizations()
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSearchClear = async () => {
    setSearchQuery('')
    setPage(0)
    setSearchLoading(true)
    try {
      const data = await storeLocationMappingService.listOrganizations({
        page: 1,
        limit: rowsPerPage,
      })
      setRows(data?.items || [])
      setTotalItems(data?.pagination?.total ?? 0)
    } finally {
      setSearchLoading(false)
    }
  }

  const openCreate = () => {
    setDialogOpen(true)
  }

  const openDelete = (e, row) => {
    e.stopPropagation()
    setDeleteTarget(row)
    setDeleteError(null)
    setDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      setDeleteLoading(true)
      setDeleteError(null)
      await storeLocationMappingService.removeAllByOrg(deleteTarget.organization_id)
      setSuccessMessage('All location links removed for this shop')
      setTimeout(() => setSuccessMessage(null), 4000)
      setDeleteOpen(false)
      setDeleteTarget(null)
      fetchOrganizations()
    } catch (e) {
      setDeleteError(e.response?.data?.message || 'Delete failed')
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "#1a1a1a", mb: 1 }}
          >
            Store location mapping
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Link locations to shops. One row per shop; open a row to view or edit
            linked locations.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ bgcolor: "#e90000", "&:hover": { bgcolor: "#cc0000" } }}
          >
            Add locations
          </Button>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchOrganizations}
              disabled={loading}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ flex: '1 1 320px', minWidth: 280 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', maxWidth: 500 }}>
            <TextField
              label="Search Shop Id"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleSearchClear}
                      sx={{ minWidth: 'auto', p: 0.5, color: '#666' }}
                    >
                      Clear
                    </Button>
                  </InputAdornment>
                ),
              }}
              placeholder="Shop name or Shop Id…"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={searchLoading}
              sx={{
                bgcolor: '#e90000',
                '&:hover': { bgcolor: '#cc0000' },
                minWidth: 'auto',
                px: 2,
              }}
            >
              {searchLoading ? <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} /> : null}
              {searchLoading ? 'Searching…' : 'Search'}
            </Button>
          </Box>
        </form>
      </Box>

      <Card
        sx={{
          borderRadius: 2,
          boxShadow:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: 48 }} />
                  <TableCell sx={{ fontWeight: 'bold' }}>Shop Id</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Shop Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Locations
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last modified</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No shops with location links yet. Use &quot;Add locations&quot;.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.organization_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/store-location-mapping/${row.organization_id}`)}
                    >
                      <TableCell>
                        <LocationOnIcon sx={{ color: '#e90000', opacity: 0.85 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.organization_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {orgRowLabel(row)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {row.location_count ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(row.last_modified)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove all links for this shop">
                          <IconButton
                            size="small"
                            onClick={(e) => openDelete(e, row)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      <AddOrgMappingDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        onSaved={(msg) => {
          setSuccessMessage(msg || 'Saved')
          setTimeout(() => setSuccessMessage(null), 4000)
          fetchOrganizations()
        }}
      />

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteLoading && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Remove all location links for this shop?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Shop Id: <strong>{deleteTarget ? deleteTarget.organization_id : ''}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Name: <strong>{deleteTarget ? orgRowLabel(deleteTarget) : ''}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
            Linked locations: {deleteTarget?.location_count ?? 0}. This cannot be undone from this
            screen except by linking locations again.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
}

export default StoreLocationMappingPage
